"use client";

import { Api } from "@/app/api";
import { buildWafSafeServicePatchChunks } from "@/lib/utils/waf-safe-service-patch";
import type {
	Service,
	ServiceFrequentlyBought,
	ServiceReview,
	ServiceStatus,
	CreateServiceDto,
	ImportEnvatoServiceDto,
	SyncEnvatoServiceDto,
	ServicesApiResponse,
} from "@/types";

export const serviceInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getServices: builder.query<{ docs: Service[] }, void>({
			query: () => ({
				url: "/services",
			}),
			providesTags: ["services"],
		}),
		getService: builder.query<Service, string>({
			query: (id) => `/services/${id}`,
			providesTags: ["services"],
		}),
		getServiceBySearch: builder.query<{ docs: Service[] }, string>({
			query: (id) => `/services?${id}`,
			providesTags: ["services"],
		}),
		getCustomServicesByIds: builder.query<{ docs: Service[] }, string>({
			query: (ids) => `/services?page=1&pagination=true&ids=${ids}&isCustomOffer=true`,
			providesTags: ["services"],
		}),
		getServicesByIds: builder.query<{ docs: Service[] }, string>({
			query: (ids) => `/services?page=1&pagination=true&ids=${ids}`,
			providesTags: ["services"],
		}),
		getRatingByServiceId: builder.query<ServiceReview[], string>({
			query: (ids) => `/services/${ids}/feedbacks`,
		}),
		createService: builder.mutation<Service, CreateServiceDto>({
			query: (body) => ({
				url: "/services",
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["category"],
		}),
		importEnvatoService: builder.mutation<Service, ImportEnvatoServiceDto>({
			query: (body) => ({
				url: "/services/import-envato",
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["category", "services"],
		}),
		syncEnvatoService: builder.mutation<
			Service,
			{ serviceId: string; body: SyncEnvatoServiceDto }
		>({
			query: ({ serviceId, body }) => ({
				url: `/services/${serviceId}/sync-envato`,
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
				...(body.rehostDetailsImagesOnS3
					? { timeout: 300_000 }
					: {}),
			}),
			invalidatesTags: ["category", "services"],
		}),
		editService: builder.mutation<Service, { serviceId: string; body: Partial<CreateServiceDto> }>(
			{
				async queryFn({ serviceId, body }, _api, _extra, baseQuery) {
					const chunks = await buildWafSafeServicePatchChunks(
						body as Record<string, unknown>,
					);
					let last: Service | undefined;
					for (const chunk of chunks) {
						const result = await baseQuery({
							url: `/services/${serviceId}`,
							method: "PATCH",
							body: JSON.stringify(chunk),
							headers: {
								"Content-Type": "application/json",
							},
						});
						if (result.error) return { error: result.error };
						last = result.data as Service;
					}
					if (!last) {
						return { error: { status: 400, data: { message: "Nothing to save" } } };
					}
					return { data: last };
				},
				invalidatesTags: ["services"],
			},
		),
		deleteService: builder.mutation<{ message: string; filesDeleted: number }, string>({
			query: (serviceId) => ({
				url: `/services/${serviceId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["services"],
		}),
		updateService: builder.mutation<void, { serviceId: string; body: { isPopular: boolean } }>({
			query: ({ serviceId, body }) => ({
				url: `/services/${serviceId}`,
				method: "PATCH",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["services"],
		}),
		searchServices: builder.query<
			ServicesApiResponse,
			{
				search?: string;
				sortBy?: string;
				page?: number;
				limit?: number;
				categoryID?: string;
				subcategoryIds?: string;
				isCustomOffer?: boolean;
				status?: ServiceStatus;
				slug?: string;
				includeAll?: boolean;
				/** Parent catalog listings only (excludes child add-ons). */
				catalogParentsOnly?: boolean;
				/** When false, backend returns full matching set (admin lists). */
				pagination?: boolean;
			}
		>({
			query: ({
				search,
				sortBy,
				page,
				limit,
				categoryID,
				subcategoryIds,
				isCustomOffer,
				status,
				slug,
				includeAll,
				catalogParentsOnly,
				pagination,
			}) => {
				/** Never send `subcategoryIds=undefined` / empty — backend would AND a bogus tag filter. */
				const params: Record<string, string | number | boolean> = {
					page: page ?? 1,
					limit: limit ?? 10,
				};
				if (categoryID?.trim()) params.category = categoryID.trim();
				const sub = subcategoryIds?.trim();
				if (sub) params.subcategoryIds = sub;
				const q = search?.trim();
				if (q) params.search = q;
				if (sortBy?.trim()) params.sortBy = sortBy.trim();
				if (isCustomOffer === true) params.isCustomOffer = true;
				if (slug?.trim()) params.slug = slug.trim();
				if (status) params.status = status;
				if (includeAll === true) params.includeAll = true;
				if (catalogParentsOnly === true) params.catalogParentsOnly = true;
				if (pagination === false) params.pagination = false;
				return {
					url: `/services`,
					params,
					headers: {
						"Content-Type": "application/json",
					},
				};
			},
			providesTags: ["services"],
		}),
		getFBTServices: builder.query<ServiceFrequentlyBought, string>({
			query: (id) => `/services/${id}/frequently-bought-together`,
			providesTags: ["services-frequently-bought"],
		}),
		setFBTServices: builder.mutation<void, { serviceID: string; body: string[] }>({
			query: ({ serviceID, body }) => ({
				url: `/services/${serviceID}/frequently-bought-together`,
				method: "POST",
				body: JSON.stringify({
					serviceIds: body,
				}),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["services-frequently-bought"],
		}),
		updateFBTServices: builder.mutation<
			void,
			{ id: string; serviceId: string; body: string[] }
		>({
			query: ({ id, serviceId, body }) => ({
				url: `/services/${serviceId}/frequently-bought-together/${id}`,
				method: "PATCH",
				body: JSON.stringify({ serviceIds: body }),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["services-frequently-bought"],
		}),
		setFeedBack: builder.mutation<
			void,
			{
				serviceID: string;
				body: {
					orderId: string;
					subOrderId: string;
					rating: number;
					comment: string;
					videoUrl?: string;
				};
			}
		>({
			query: ({ serviceID, body }) => ({
				url: `/services/${serviceID}/feedbacks`,
				method: "POST",
				body: {
					orderId: body.orderId,
					subOrderId: body.subOrderId,
					rating: body.rating,
					comment: body.comment,
					...(body.videoUrl && { videoUrl: body.videoUrl }),
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
		}),
	}),
});

export const {
	useGetServicesQuery,
	useGetServiceQuery,
	useGetServicesByIdsQuery,
	useGetRatingByServiceIdQuery,
	useSearchServicesQuery,
	useLazySearchServicesQuery,
	useCreateServiceMutation,
	useImportEnvatoServiceMutation,
	useSyncEnvatoServiceMutation,
	useUpdateServiceMutation,
	useGetServiceBySearchQuery,
	useGetFBTServicesQuery,
	useLazyGetFBTServicesQuery,
	useSetFBTServicesMutation,
	useUpdateFBTServicesMutation,
	useSetFeedBackMutation,
	useEditServiceMutation,
	useDeleteServiceMutation,
	useGetCustomServicesByIdsQuery,
} = serviceInjection;
